import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
    Shield, LogIn, LogOut, Layers, Users, Tag, Coins, FolderPlus, X,
    PackageOpen, PackageCheck, PackageX, ImagePlus, Loader2, Pencil, Check, MessageCircle, PlusCircle, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { formatIDR, waLink, formatPhone, formatApiErrorDetail } from "../lib/api";
import { resizeImage } from "../lib/image";
import { useApp } from "../context/AppContext";
import { CATEGORIES } from "../lib/images";

const EMPTY_FORM = {
    title: "", category: "Tempat Pensil", material: "", price: "", phone: "",
    creatorName: "", creatorBio: "", description: "", soldOut: false,
};

export default function AdminDashboard() {
    const { user, login, logout } = useApp();
    const queryClient = useQueryClient();
    const isAdmin = user !== undefined && user?.role === "admin";

    const fileRef = useRef(null);
    const formRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [images, setImages] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [colTitle, setColTitle] = useState("");
    const [colDesc, setColDesc] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [colSaving, setColSaving] = useState(false);
    const [catName, setCatName] = useState("");
    const [renameId, setRenameId] = useState(null);
    const [renameVal, setRenameVal] = useState("");
    const [catSaving, setCatSaving] = useState(false);

    const { data: products, isLoading } = useQuery({
        queryKey: ["admin-products"],
        queryFn: async () => (await api.get("/products")).data,
        enabled: isAdmin,
    });

    const { data: collections } = useQuery({
        queryKey: ["admin-collections"],
        queryFn: async () => (await api.get("/collections")).data,
        enabled: isAdmin,
    });

    const { data: kategoris } = useQuery({
        queryKey: ["categories"],
        queryFn: async () => (await api.get("/categories")).data,
        enabled: isAdmin,
    });

    useEffect(() => {
        if (!isAdmin) {
            setEditingId(null);
            setForm(EMPTY_FORM);
            setImages([]);
        }
    }, [isAdmin]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

    const stats = products
        ? [
              { icon: Layers, label: "Total Karya", value: products.length },
              { icon: PackageCheck, label: "Tersedia", value: products.filter((p) => !p.sold_out).length },
              { icon: PackageX, label: "Sold Out", value: products.filter((p) => p.sold_out).length },
              { icon: Users, label: "Kreator", value: new Set(products.map((p) => p.creator_name)).size },
              { icon: Tag, label: "Kategori", value: new Set(products.map((p) => p.category)).size },
              { icon: Coins, label: "Nilai Katalog", value: formatIDR(products.reduce((s, p) => s + (p.price || 0), 0)) },
          ]
        : [];

    const pickFile = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (files.length === 0) return;
        const sisa = 5 - images.length;
        if (sisa <= 0) {
            toast.error("Maksimal 5 foto per produk");
            return;
        }
        if (files.length > sisa) toast.error("Maksimal 5 foto — sisanya dilewati");
        const hasil = [];
        for (const file of files.slice(0, sisa)) {
            if (file.size > 8 * 1024 * 1024) {
                toast.error(`"${file.name}" lebih dari 8MB, dilewati`);
                continue;
            }
            try {
                hasil.push(await resizeImage(file));
            } catch {
                toast.error("Gagal membaca foto, coba yang lain");
            }
        }
        if (hasil.length > 0) setImages((prev) => [...prev, ...hasil].slice(0, 5));
    };

    const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

    const startEdit = (p) => {
        setEditingId(p.id);
        setForm({
            title: p.title || "",
            category: p.category || "Tempat Pensil",
            material: p.material || "",
            price: p.price || "",
            phone: p.phone || "",
            creatorName: p.creator_name || "",
            creatorBio: p.creator_bio || "",
            description: p.description || "",
            soldOut: !!p.sold_out,
        });
        setImages(p.images?.length ? p.images : p.image ? [p.image] : []);
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setImages([]);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (images.length === 0) {
            toast.error("Pilih minimal 1 foto karya dulu ya");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                title: form.title,
                category: form.category,
                material: form.material,
                price: Number(form.price || 0),
                phone: form.phone,
                creator_name: form.creatorName,
                creator_bio: form.creatorBio,
                description: form.description,
                sold_out: form.soldOut,
                image: images[0],
                images,
            };
            if (editingId) {
                await api.put(`/products/${editingId}`, payload);
                toast.success("Perubahan karya tersimpan!");
            } else {
                await api.post("/products", payload);
                toast.success("Karya baru sudah tayang di katalog!");
            }
            await queryClient.invalidateQueries({ queryKey: ["admin-products"] });
            await queryClient.invalidateQueries({ queryKey: ["products"] });
            await queryClient.invalidateQueries({ queryKey: ["collections"] });
            cancelEdit();
        } catch (err) {
            toast.error("Gagal menyimpan karya — coba lagi ya");
        } finally {
            setLoading(false);
        }
    };

    const toggleSold = async (p) => {
        try {
            await api.put(`/products/${p.id}`, {
                title: p.title, category: p.category, material: p.material, price: p.price,
                description: p.description, image: p.image, phone: p.phone,
                creator_name: p.creator_name, creator_bio: p.creator_bio, sold_out: !p.sold_out,
                images: p.images?.length ? p.images : (p.image ? [p.image] : []),
            });
            await queryClient.invalidateQueries({ queryKey: ["admin-products"] });
            await queryClient.invalidateQueries({ queryKey: ["products"] });
            await queryClient.invalidateQueries({ queryKey: ["collections"] });
            toast.success(p.sold_out ? `"${p.title}" diaktifkan kembali` : `"${p.title}" ditandai sold out`);
        } catch {
            toast.error("Gagal mengubah status karya");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Hapus listing "${p.title}"?`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            await queryClient.invalidateQueries({ queryKey: ["admin-products"] });
            await queryClient.invalidateQueries({ queryKey: ["products"] });
            await queryClient.invalidateQueries({ queryKey: ["collections"] });
            toast.success("Listing dihapus dari katalog");
        } catch {
            toast.error("Gagal menghapus listing");
        }
    };

    const toggleProduct = (id) => {
        setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    };

    const createCollection = async (e) => {
        e.preventDefault();
        if (selectedIds.length === 0) {
            toast.error("Pilih minimal 1 karya untuk koleksinya");
            return;
        }
        setColSaving(true);
        try {
            await api.post("/collections", { title: colTitle, description: colDesc, product_ids: selectedIds });
            toast.success("Koleksi kurasi dibuat!");
            setColTitle("");
            setColDesc("");
            setSelectedIds([]);
            await queryClient.invalidateQueries({ queryKey: ["admin-collections"] });
            await queryClient.invalidateQueries({ queryKey: ["collections"] });
        } catch {
            toast.error("Gagal membuat koleksi");
        } finally {
            setColSaving(false);
        }
    };

    const deleteCollection = async (c) => {
        if (!window.confirm(`Hapus koleksi "${c.title}"?`)) return;
        try {
            await api.delete(`/collections/${c.id}`);
            await queryClient.invalidateQueries({ queryKey: ["admin-collections"] });
            await queryClient.invalidateQueries({ queryKey: ["collections"] });
            toast.success("Koleksi dihapus");
        } catch {
            toast.error("Gagal menghapus koleksi");
        }
    };

    const invalidateCatalog = async () => {
        await queryClient.invalidateQueries({ queryKey: ["categories"] });
        await queryClient.invalidateQueries({ queryKey: ["admin-products"] });
        await queryClient.invalidateQueries({ queryKey: ["products"] });
        await queryClient.invalidateQueries({ queryKey: ["collections"] });
    };

    const addCategory = async (e) => {
        e.preventDefault();
        if (catName.trim().length < 2) {
            toast.error("Nama kategori minimal 2 huruf");
            return;
        }
        setCatSaving(true);
        try {
            await api.post("/categories", { name: catName.trim() });
            toast.success("Kategori baru ditambahkan!");
            setCatName("");
            await invalidateCatalog();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setCatSaving(false);
        }
    };

    const saveRename = async () => {
        if (renameVal.trim().length < 2) {
            toast.error("Nama kategori minimal 2 huruf");
            return;
        }
        setCatSaving(true);
        try {
            await api.put(`/categories/${renameId}`, { name: renameVal.trim() });
            toast.success("Nama kategori diubah — karya di katalog ikut diperbarui");
            setRenameId(null);
            await invalidateCatalog();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setCatSaving(false);
        }
    };

    const inputCls =
        "w-full px-4 py-3 rounded-2xl border-2 border-brand-ink bg-white text-sm font-semibold placeholder:text-brand-ink/40 placeholder:font-medium focus:outline-none focus:ring-4 focus:ring-brand-sunshine/60";

    return (
        <>
            <Navbar />
            <main className="pt-24 md:pt-28 pb-16 md:pb-24 min-h-screen">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-brand-ink bg-brand-carrot text-white shadow-hard-sm">
                                <Shield className="w-4 h-4" strokeWidth={2.6} />
                                <span className="text-xs font-extrabold uppercase tracking-[0.16em]">Dashboard Admin</span>
                            </div>
                            <h1 data-testid="admin-dashboard-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                                Kelola Acture<span className="text-brand-forest">.id</span>
                            </h1>
                        </div>
                        {user && (
                            <button
                                data-testid="dashboard-logout-button"
                                onClick={logout}
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-brand-ink bg-white font-bold text-sm shadow-hard-sm hover:bg-brand-tomato hover:text-white transition-all active:translate-y-0.5"
                            >
                                <LogOut className="w-4 h-4" strokeWidth={2.6} /> Logout
                            </button>
                        )}
                    </div>

                    {user === undefined && <div className="mt-10 h-64 rounded-[26px] bg-white border-2 border-brand-ink/10 animate-pulse" />}

                    {!isAdmin && user !== undefined && (
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-10 max-w-md mx-auto rounded-[26px] border-2 border-brand-ink bg-white p-8 shadow-hard"
                        >
                            <span className="grid place-items-center w-14 h-14 rounded-2xl border-2 border-brand-ink bg-brand-forestLight text-brand-forest shadow-hard-sm">
                                <Shield className="w-7 h-7" strokeWidth={2.4} />
                            </span>
                            <h2 className="font-display text-2xl font-extrabold mt-5">Masuk Admin</h2>
                            <p className="mt-1.5 text-sm text-brand-ink/60">
                                Dashboard ini khusus admin Acture.id. Produsen kertas? Daftar jadi kreator lewat
                                <a href={waLink("Halo Acture.id! Saya produsen kerajinan kertas, mau daftar jadi kreator.")} target="_blank" rel="noreferrer" className="font-bold text-brand-forest underline underline-offset-2"> WhatsApp CS (0813-8319-664)</a>.
                            </p>
                            <form
                                data-testid="dashboard-login-form"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    const fd = new FormData(e.target);
                                    await login(fd.get("email"), fd.get("password"));
                                }}
                                className="mt-6 flex flex-col gap-3"
                            >
                                <input data-testid="dashboard-login-email-input" name="email" required type="email" placeholder="Email admin" className={inputCls} />
                                <input data-testid="dashboard-login-password-input" name="password" required type="password" placeholder="Password" className={inputCls} />
                                <button
                                    data-testid="dashboard-login-button"
                                    type="submit"
                                    className="w-full py-4 rounded-full border-2 border-brand-ink bg-brand-forest text-brand-cream font-display font-bold shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0 active:shadow-hard-sm"
                                >
                                    Masuk Dashboard
                                </button>
                            </form>
                        </motion.div>
                    )}

                    {isAdmin && (
                        <>
                            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                {stats.map((s, i) => (
                                    <motion.div
                                        key={s.label}
                                        initial={{ opacity: 0, y: 24 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: i * 0.05 }}
                                        className="rounded-[22px] border-2 border-brand-ink bg-white p-5 shadow-hard-sm"
                                    >
                                        <s.icon className="w-5 h-5 text-brand-forest" strokeWidth={2.4} />
                                        <p data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, "-")}`} className="mt-3 font-display text-2xl font-extrabold">{s.value}</p>
                                        <p className="text-[11px] font-bold text-brand-ink/60 uppercase tracking-wide">{s.label}</p>
                                    </motion.div>
                                ))}
                            </div>

                            <div ref={formRef} data-testid="product-form-section" className="mt-10 scroll-mt-28 rounded-[28px] border-2 border-brand-ink bg-white shadow-hard overflow-hidden">
                                <div className="flex flex-wrap items-center justify-between gap-3 px-7 pt-7">
                                    <h2 className="font-display text-2xl font-extrabold flex items-center gap-2">
                                        {editingId ? <Pencil className="w-6 h-6 text-brand-carrot" strokeWidth={2.6} /> : <PlusCircle className="w-6 h-6 text-brand-forest" strokeWidth={2.6} />}
                                        {editingId ? "Edit Karya" : "Tambah Karya Baru"}
                                    </h2>
                                    {editingId && (
                                        <button
                                            data-testid="product-form-cancel-button"
                                            onClick={cancelEdit}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-brand-ink bg-white text-xs font-bold hover:bg-brand-sunshine/50 transition-colors"
                                        >
                                            <X className="w-3.5 h-3.5" strokeWidth={3} /> Batal Edit
                                        </button>
                                    )}
                                </div>

                                <form data-testid="product-form" onSubmit={submit} className="p-7 pt-5 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
                                    <div className="flex flex-col gap-3">
                                        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={pickFile} />
                                        {images[0] ? (
                                            <div className="relative w-full aspect-[4/3] rounded-2xl border-2 border-brand-ink overflow-hidden bg-brand-cream">
                                                <img src={images[0]} alt="Foto utama karya" className="w-full h-full object-cover" />
                                                <span className="absolute top-2 left-2 px-2.5 py-1 rounded-full border-2 border-brand-ink bg-brand-sunshine text-[10px] font-extrabold uppercase tracking-wide">Foto Utama</span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                data-testid="product-form-photo-button"
                                                onClick={() => fileRef.current?.click()}
                                                className="relative w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-brand-ink/50 overflow-hidden bg-brand-cream grid place-items-center hover:border-brand-forest transition-colors"
                                            >
                                                <span className="flex flex-col items-center gap-2 text-brand-ink/60">
                                                    <ImagePlus className="w-9 h-9" strokeWidth={2} />
                                                    <span className="text-sm font-bold">Pilih / fotoin karya</span>
                                                    <span className="text-xs">JPG/PNG, maks 8MB — bisa pilih beberapa foto sekaligus</span>
                                                </span>
                                            </button>
                                        )}
                                        {images.length > 0 && (
                                            <div className="grid grid-cols-4 gap-2">
                                                {images.map((img, i) => (
                                                    <div key={i} data-testid={`product-form-photo-thumb-${i}`} className="relative aspect-square rounded-xl border-2 border-brand-ink overflow-hidden">
                                                        <img src={img} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                                                        {i > 0 && (
                                                            <button
                                                                type="button"
                                                                data-testid={`product-form-photo-remove-${i}`}
                                                                onClick={() => removeImage(i)}
                                                                aria-label={`Hapus foto ${i + 1}`}
                                                                className="absolute top-1 right-1 p-1 rounded-full border-2 border-brand-ink bg-white text-brand-tomato hover:bg-brand-tomato hover:text-white transition-colors"
                                                            >
                                                                <Trash2 className="w-3 h-3" strokeWidth={3} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                {images.length < 5 && (
                                                    <button
                                                        type="button"
                                                        data-testid="product-form-photo-add-button"
                                                        onClick={() => fileRef.current?.click()}
                                                        aria-label="Tambah foto"
                                                        className="aspect-square rounded-xl border-2 border-dashed border-brand-ink/50 grid place-items-center text-brand-ink/50 hover:border-brand-forest hover:text-brand-forest transition-colors"
                                                    >
                                                        <ImagePlus className="w-5 h-5" strokeWidth={2.2} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {images.length > 0 && (
                                            <p className="text-xs font-bold text-brand-ink/60">{images.length}/5 foto — foto pertama jadi foto utama katalog</p>
                                        )}
                                        <label className="flex items-center justify-between gap-3 rounded-2xl border-2 border-brand-ink bg-brand-cream px-4 py-3.5 cursor-pointer">
                                            <span className="text-sm font-bold inline-flex items-center gap-2">
                                                <PackageX className="w-4 h-4 text-brand-tomato" strokeWidth={2.6} /> Sold Out (stok habis)
                                            </span>
                                            <input data-testid="product-form-soldout-toggle" type="checkbox" checked={form.soldOut} onChange={setBool("soldOut")} className="w-5 h-5 accent-[#D52518]" />
                                        </label>
                                    </div>

                                    <div className="flex flex-col gap-3.5">
                                        <input data-testid="product-form-title-input" required minLength={3} value={form.title} onChange={set("title")} placeholder="Judul karya (contoh: Tempat Pensil Gulung Kertas Pop Art)" className={inputCls} />
                                        <div className="grid grid-cols-2 gap-3">
                                            <select data-testid="product-form-category-input" value={form.category} onChange={set("category")} className={inputCls}>
                                                {(kategoris?.length ? kategoris.map((c) => c.name) : CATEGORIES.filter((c) => c !== "Semua Karya")).map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                            <input data-testid="product-form-price-input" required type="number" min="0" value={form.price} onChange={set("price")} placeholder="Harga (Rp)" className={inputCls} />
                                        </div>
                                        <input data-testid="product-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="No. WhatsApp/Telepon produk — order langsung link ke nomor ini (kosongkan = CS 0813-8319-664)" className={inputCls} />
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <input data-testid="product-form-creator-name-input" required minLength={2} value={form.creatorName} onChange={set("creatorName")} placeholder="Nama kreator / produsen" className={inputCls} />
                                            <input data-testid="product-form-material-input" value={form.material} onChange={set("material")} placeholder="Bahan daur ulang (contoh: Kertas HVS Bekas)" className={inputCls} />
                                        </div>
                                        <input data-testid="product-form-creator-bio-input" value={form.creatorBio} onChange={set("creatorBio")} placeholder="Deskripsi singkat kreator (tampil di detail produk & profil kreator)" className={inputCls} />
                                        <textarea data-testid="product-form-description-input" value={form.description} onChange={set("description")} placeholder="Deskripsi produk: ukuran, cara buat, keunikan..." rows={3} className={`${inputCls} resize-none`} />
                                        <button
                                            data-testid="product-form-submit-button"
                                            type="submit"
                                            disabled={loading}
                                            className="w-full sm:w-fit px-8 py-4 rounded-full border-2 border-brand-ink bg-brand-forest text-brand-cream font-display font-bold shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0 active:shadow-hard-sm disabled:opacity-60 disabled:pointer-events-none inline-flex items-center justify-center gap-2"
                                        >
                                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={3} />}
                                            {editingId ? "Simpan Perubahan" : "Tayangkan Karya"}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            <div className="mt-10 rounded-[26px] border-2 border-brand-ink bg-white shadow-hard-sm overflow-hidden">
                                <div className="px-6 pt-6 pb-2">
                                    <h2 className="font-display text-2xl font-extrabold">Semua Karya ({products?.length || 0})</h2>
                                    <p className="text-sm text-brand-ink/60 mt-1">Kelola katalog: edit, tandai sold out, atau hapus listing.</p>
                                </div>
                                {isLoading ? (
                                    <div className="p-6 space-y-4">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="h-14 bg-brand-ink/10 rounded-xl animate-pulse" />
                                        ))}
                                    </div>
                                ) : products && products.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full min-w-[900px] text-sm">
                                            <thead>
                                                <tr className="bg-brand-cream text-left text-xs font-extrabold uppercase tracking-wide text-brand-ink/60">
                                                    <th className="px-5 py-4">Karya</th>
                                                    <th className="px-5 py-4">Kreator</th>
                                                    <th className="px-5 py-4">Kontak</th>
                                                    <th className="px-5 py-4">Harga</th>
                                                    <th className="px-5 py-4">Status</th>
                                                    <th className="px-5 py-4 text-right">Aksi</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {products.map((p) => (
                                                    <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-t-2 border-brand-ink/5 hover:bg-brand-sunshine/15 transition-colors">
                                                        <td className="px-5 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <img src={p.image} alt={p.title} className="w-12 h-12 rounded-xl border-2 border-brand-ink object-cover shrink-0" />
                                                                <span className="font-bold line-clamp-1 max-w-[200px]">{p.title}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-4 font-semibold text-brand-ink/70">{p.creator_name}</td>
                                                        <td className="px-5 py-4 font-semibold text-brand-ink/70">{p.phone ? formatPhone(p.phone) : <span className="text-brand-ink/40">CS</span>}</td>
                                                        <td className="px-5 py-4 font-display font-extrabold">{formatIDR(p.price)}</td>
                                                        <td className="px-5 py-4 whitespace-nowrap">
                                                            <button
                                                                data-testid={`admin-soldout-toggle-${p.id}`}
                                                                onClick={() => toggleSold(p)}
                                                                className={`px-3 py-1.5 rounded-full border-2 border-brand-ink text-[11px] font-extrabold shadow-hard-sm transition-all active:translate-y-0.5 active:shadow-none ${
                                                                    p.sold_out ? "bg-brand-tomato text-brand-cream" : "bg-brand-forestLight text-brand-forest"
                                                                }`}
                                                            >
                                                                {p.sold_out ? "SOLD OUT" : "TERSEDIA"}
                                                            </button>
                                                        </td>
                                                        <td className="px-5 py-4 whitespace-nowrap">
                                                            <div className="flex items-center justify-end gap-1.5">
                                                                <button
                                                                    data-testid={`admin-edit-button-${p.id}`}
                                                                    onClick={() => startEdit(p)}
                                                                    aria-label={`Edit ${p.title}`}
                                                                    className="p-2.5 rounded-full border-2 border-brand-ink bg-white text-brand-forest hover:bg-brand-forest hover:text-brand-cream transition-colors"
                                                                >
                                                                    <Pencil className="w-4 h-4" strokeWidth={2.6} />
                                                                </button>
                                                                <button
                                                                    data-testid={`admin-delete-button-${p.id}`}
                                                                    onClick={() => remove(p)}
                                                                    aria-label={`Hapus ${p.title}`}
                                                                    className="p-2.5 rounded-full border-2 border-brand-ink bg-white text-brand-tomato hover:bg-brand-tomato hover:text-white transition-colors"
                                                                >
                                                                    <Trash2 className="w-4 h-4" strokeWidth={2.6} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="py-16 text-center">
                                        <PackageOpen className="w-10 h-10 mx-auto text-brand-ink/50" />
                                        <p className="mt-4 font-display text-xl font-bold">Belum ada karya</p>
                                        <p className="mt-1.5 text-sm text-brand-ink/60">Tambah karya pertama lewat form di atas.</p>
                                    </div>
                                )}
                            </div>

                            <div data-testid="admin-categories-section" className="mt-10 rounded-[26px] border-2 border-brand-ink bg-white shadow-hard-sm p-7">
                                <h2 className="font-display text-2xl font-extrabold flex items-center gap-2">
                                    <Tag className="w-6 h-6 text-brand-carrot" strokeWidth={2.4} /> Kelola Kategori
                                </h2>
                                <p className="mt-1.5 text-sm text-brand-ink/70">Tambah kategori baru atau ganti nama — karya di katalog otomatis ikut berubah.</p>

                                <form onSubmit={addCategory} className="mt-5 flex flex-col sm:flex-row gap-3">
                                    <input
                                        data-testid="admin-category-add-input"
                                        value={catName}
                                        onChange={(e) => setCatName(e.target.value)}
                                        placeholder="Nama kategori baru (contoh: Hiasan Dinding)"
                                        className={inputCls}
                                    />
                                    <button
                                        data-testid="admin-category-add-button"
                                        type="submit"
                                        disabled={catSaving}
                                        className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-brand-ink bg-brand-forest text-brand-cream font-display font-bold shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0 disabled:opacity-60"
                                    >
                                        {catSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" strokeWidth={2.6} />}
                                        Tambah Kategori
                                    </button>
                                </form>

                                <div className="mt-5 flex flex-wrap gap-2.5">
                                    {kategoris?.map((c) => (
                                        renameId === c.id ? (
                                            <div key={c.id} className="inline-flex items-center gap-1.5 rounded-full border-2 border-brand-ink bg-brand-cream pl-4 pr-1.5 py-1">
                                                <input
                                                    data-testid={`admin-category-rename-input-${c.id}`}
                                                    autoFocus
                                                    value={renameVal}
                                                    onChange={(e) => setRenameVal(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveRename(); } }}
                                                    className="bg-transparent text-sm font-bold outline-none w-44"
                                                />
                                                <button
                                                    data-testid={`admin-category-save-button-${c.id}`}
                                                    type="button"
                                                    onClick={saveRename}
                                                    disabled={catSaving}
                                                    aria-label="Simpan nama kategori"
                                                    className="p-1.5 rounded-full border-2 border-brand-ink bg-brand-forest text-brand-cream hover:opacity-85 transition-opacity"
                                                >
                                                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setRenameId(null)}
                                                    aria-label="Batal ganti nama"
                                                    className="p-1.5 rounded-full border-2 border-brand-ink bg-white text-brand-tomato hover:bg-brand-tomato hover:text-white transition-colors"
                                                >
                                                    <X className="w-3.5 h-3.5" strokeWidth={3} />
                                                </button>
                                            </div>
                                        ) : (
                                            <div key={c.id} className="inline-flex items-center gap-1 rounded-full border-2 border-brand-ink bg-white pl-4 pr-1.5 py-1">
                                                <span data-testid={`admin-category-chip-${c.id}`} className="text-sm font-bold">{c.name}</span>
                                                <button
                                                    data-testid={`admin-category-rename-button-${c.id}`}
                                                    type="button"
                                                    onClick={() => { setRenameId(c.id); setRenameVal(c.name); }}
                                                    aria-label={`Ganti nama kategori ${c.name}`}
                                                    className="p-1.5 rounded-full hover:bg-brand-sunshine/50 transition-colors"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" strokeWidth={2.6} />
                                                </button>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>

                            <div className="mt-10 rounded-[26px] border-2 border-brand-ink bg-brand-forestLight/60 p-7 shadow-hard-sm">
                                <h2 className="font-display text-2xl font-extrabold flex items-center gap-2">
                                    <FolderPlus className="w-6 h-6 text-brand-forest" strokeWidth={2.4} /> Koleksi Kurasi
                                </h2>
                                <p className="mt-1.5 text-sm text-brand-ink/70">Rangkai karya se-tema jadi satu halaman yang bisa dibagikan.</p>

                                <form onSubmit={createCollection} className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-3">
                                        <input data-testid="admin-collection-title-input" required minLength={3} value={colTitle} onChange={(e) => setColTitle(e.target.value)} placeholder="Judul koleksi (contoh: Rapikan Meja Belajar)" className={inputCls} />
                                        <textarea data-testid="admin-collection-desc-input" value={colDesc} onChange={(e) => setColDesc(e.target.value)} placeholder="Deskripsi singkat koleksi..." rows={3} className={`${inputCls} resize-none`} />
                                        <button
                                            data-testid="admin-collection-submit-button"
                                            type="submit"
                                            disabled={colSaving}
                                            className="self-start inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-brand-ink bg-brand-forest text-brand-cream font-display font-bold shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0 disabled:opacity-60"
                                        >
                                            {colSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" strokeWidth={2.6} />}
                                            Buat Koleksi ({selectedIds.length} karya)
                                        </button>
                                    </div>
                                    <div data-lenis-prevent className="rounded-2xl border-2 border-brand-ink bg-white p-4 max-h-64 overflow-y-auto">
                                        <p className="text-xs font-extrabold uppercase tracking-wide text-brand-ink/60 mb-3">Pilih karya ({products?.length || 0} tersedia)</p>
                                        <div className="flex flex-col gap-2">
                                            {products?.map((p) => (
                                                <label key={p.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-brand-sunshine/20 cursor-pointer transition-colors">
                                                    <input
                                                        data-testid={`admin-collection-product-checkbox-${p.id}`}
                                                        type="checkbox"
                                                        checked={selectedIds.includes(p.id)}
                                                        onChange={() => toggleProduct(p.id)}
                                                        className="w-4 h-4 accent-[#18542A]"
                                                    />
                                                    <img src={p.image} alt="" className="w-9 h-9 rounded-lg border-2 border-brand-ink object-cover" />
                                                    <span className="text-sm font-bold line-clamp-1">{p.title}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </form>

                                {collections && collections.length > 0 && (
                                    <div className="mt-6 flex flex-col gap-2">
                                        {collections.map((c) => (
                                            <div key={c.id} data-testid={`admin-collection-row-${c.id}`} className="flex items-center justify-between gap-3 rounded-2xl border-2 border-brand-ink bg-white px-4 py-3">
                                                <div className="min-w-0">
                                                    <p className="font-display font-bold truncate">{c.title}</p>
                                                    <p className="text-xs text-brand-ink/60">{c.products.length} karya • /koleksi/{c.id}</p>
                                                </div>
                                                <button
                                                    data-testid={`admin-collection-delete-button-${c.id}`}
                                                    onClick={() => deleteCollection(c)}
                                                    aria-label={`Hapus koleksi ${c.title}`}
                                                    className="p-2.5 rounded-full border-2 border-brand-ink bg-white text-brand-tomato hover:bg-brand-tomato hover:text-white transition-colors shrink-0"
                                                >
                                                    <X className="w-4 h-4" strokeWidth={2.8} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="mt-8 rounded-[26px] border-2 border-dashed border-brand-ink/40 bg-white/60 p-6 flex flex-wrap items-center justify-between gap-4">
                                <p className="text-sm text-brand-ink/70 font-semibold">
                                    Ada produsen baru yang mau gabung? Arahkan mereka daftar via WhatsApp CS 0813-8319-664 — produknya nanti di-input lewat form di atas.
                                </p>
                                <a
                                    data-testid="dashboard-cs-button"
                                    href={waLink("Halo Acture.id! Saya produsen kerajinan kertas, mau daftar jadi kreator.")}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-brand-ink bg-brand-wa text-white font-bold text-sm shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0"
                                >
                                    <MessageCircle className="w-4 h-4" strokeWidth={2.6} /> Daftar Produsen via WA
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </main>
            <Footer />
        </>
    );
}
